import React from 'react';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import DtvText from '@directv-digital/dds.ui.dtv-text';
import DtvButton, { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import DtvTooltip from '@directv-digital/dds.ui.dtv-tooltip';
import { useBreakPoints } from "@directv-digital/dds.utils";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { DVRIcon, OfferIcon, FavouriteChnlIcon } from "@directv-digital/fusion.atoms.icons";
import DynamicText, { DynamiceContentProps } from "@directv-digital/dds.ui.dynamic-text";

export interface FeatureItem {
  id: string;
  content: string;
  icon?: {
    type: 'dvr' | 'custom' | 'offer' | 'favourite';
    url?: string;
    alt?: string;
  };
  tooltip?: {
    content: string;
    position?: string;
    mode?: 'light' | 'dark';
  };
  color?: string;
}

export interface PriceInfo {
  content?: string;
  strikePrice?: any;
  spanText?: any;
  legal?: any;
  variant?: string;
}

// Generated by Copilot- Start - 2026-04-10 11:34:12
export interface ResolvedPricing {
  mainPrice: string;
  decimalPrice: string;
  strikePrice: string | null;
  hasEpochData: boolean;
  hasOffer: boolean;
}
// Generated by Copilot- End - 2026-04-10 11:34:12

export interface ColumnConfig {
  id: string;
  order: number;
  title?: string;
  features?: FeatureItem[];
  pricePoint?: PriceInfo;
  resolvedPricing?: ResolvedPricing;
  cta?: any;
  titleTooltip?: {
    content: string;
    position?: 'top' | 'bottom' | 'left' | 'right';
    mode?: 'light' | 'dark';
  };
  offerRibbon?: {
    content: string;
    background: string;
    color: string;
  };
  legalText?: DynamiceContentProps;
  legalTextNoOffer?: DynamiceContentProps;
}

export interface GenrePackDetailsPanelProps {
  id: string;
  columnData?: ColumnConfig[];
  regularPrice?: string;
  offerPrice?: string;
}

export function GenrePackDetailsPanel(props: GenrePackDetailsPanelProps) {
  const {
    id,
    columnData
  } = props;
  const { isMobile, isTablet } = useBreakPoints();
  const columns = Array.isArray(columnData) ? columnData : [];
  const shouldStack = isMobile;
  const sortedColumns = [...columns].sort((a, b) => Number(a.order) - Number(b.order));

  const renderFeatureIcon = (feature: FeatureItem) => {
    const iconStyle = {
      width: 24,
      height: 24,
      flexShrink: 0,
      marginRight: "16px"
    };
    switch (feature?.icon?.type) {
      case 'dvr':
        return <DtvBox sx={iconStyle}><DVRIcon /></DtvBox>;
      case 'offer':
        return <DtvBox sx={iconStyle}><OfferIcon /></DtvBox>;
      case 'favourite':
        return <DtvBox sx={iconStyle}><FavouriteChnlIcon /></DtvBox>;
      case 'custom':
        if (feature?.icon?.url) {
          return (
            <DtvBox
              alt={feature.icon.alt || feature.content}
              component="img"
              src={feature.icon.url}
              sx={iconStyle}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderFeatureItem = (feature: FeatureItem, columnIndex: number, featureIndex: number) => {
    const featureContent = (
      <DtvBox
        key={feature.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: 1
        }}
      >
        {renderFeatureIcon(feature)}
        <DtvText
          content={feature.content}
          tooltip={feature.tooltip?.content ? { content: feature.tooltip.content } : undefined}
          variant="body2"
          sx={{
            fontSize: '16px',
            lineHeight: 1.4,
            display: 'inline',
            color: feature?.color
          }}
        />
      </DtvBox>
    );
    return featureContent;
  };
  // Generated by Copilot- Start - 2026-04-10 11:34:12
  /**
   * Derive display prices for a column. Prefers adapter-resolved pricing
   * (resolvedPricing) when available, otherwise falls back to raw CMS pricePoint.
   * This keeps the component purely presentational with no pricing logic.
   */
  const getColumnPrices = (column: ColumnConfig) => {
    const resolved = column.resolvedPricing;
    if (resolved) {
      return {
        mainPrice: resolved.mainPrice,
        decimalPrice: resolved.decimalPrice,
        strikePriceToShow: resolved.strikePrice
      };
    }
    // Fallback: no adapter-resolved pricing, use raw CMS values
    const pricePoint = column.pricePoint || {} as PriceInfo;
    return {
      mainPrice: pricePoint?.content || "",
      decimalPrice: pricePoint?.spanText?.content || "",
      strikePriceToShow: pricePoint?.strikePrice?.content || null
    };
  };
  // Generated by Copilot- End - 2026-04-10 11:34:12

  const renderColumn = (column: ColumnConfig, index: number, isTabletLeft: boolean) => {
    const isCtaOnlyColumn = (!column.features || column.features.length === 0) && !!column.cta?.label;
    const features = Array.isArray(column.features) ? column.features : [];
    const pricePoint = column.pricePoint || {};
    const { mainPrice, decimalPrice, strikePriceToShow } = getColumnPrices(column);
    const ctaOnlySx = isCtaOnlyColumn ?
      {
        justifyContent: 'flex-start',
        alignItems: 'center',
        paddingTop: isTablet ? '120px' : { sm: '32px' },
        ...(isTablet ? {} : { marginTop: { xs: "0px" } })
      } :
      {};
    return (
      <DtvBox
        key={column.id}
        sx={{
          flex: 1,
          maxWidth:
            isTablet ?
              (isTabletLeft ? '100%' : '100%') :
              shouldStack ?
                '100%' :
                `${100 / columns?.length}%`,
          padding: "10px",
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          ...(shouldStack && index > 0 && {
 marginTop: {
 xs: 1,
sm: 3
}
}),
          ...(!shouldStack && index > 0 && { marginLeft: 2 }),
          ...ctaOnlySx
        }}
      >
        {/* Features Section */}
        {!isCtaOnlyColumn && column?.title && (
          <DtvBox sx={{
 display: 'flex',
alignItems: 'center',
flexDirection: 'row',
marginBottom: "12px"
}}>
            {column?.titleTooltip?.content && (
              <DtvTooltip
                content={column?.titleTooltip?.content}
                mode={column.titleTooltip?.mode || 'dark'}
                placement={column?.titleTooltip?.position || 'top'}
                sx={{ cursor: "pointer" }}
              >
              </DtvTooltip>
            )}
            <DtvText
              content={column.title}
              variant="body2medium"
              sx={{
 fontWeight: 500,
fontSize: "16px !important",
color: "#000",
marginLeft: column?.titleTooltip?.content ? '8px' : 0
}}
            />
          </DtvBox>
        )}
        <DtvBox>
          {features.map((feature, featureIndex) => renderFeatureItem(feature, index, featureIndex))}
        </DtvBox>
        {/* Price Section */}
        {!isCtaOnlyColumn && pricePoint && (
        <DtvBox sx={{ marginBottom: 1 }}>

          {/* Special Offer Badge — shown when CMS provides ribbon content AND either
              no adapter pricing exists (backward compat) or adapter confirms a real offer */}
          {column?.offerRibbon?.content && (!column?.resolvedPricing || column.resolvedPricing.hasOffer) && (
            <DtvBox
              sx={{
                display: 'inline-block',
                background: column?.offerRibbon?.background,
                color: column?.offerRibbon?.color,
                padding: '2px 4px',
                borderRadius: '2px'
              }}
            >
              <DtvText
                content={column.offerRibbon?.content}
                variant="smalltextmedium"
                sx={{
 fontSize: '12px !important',
fontWeight: 500
}}
              />
            </DtvBox>
          )}

          {/* Original/Strike-through Price */}
          {strikePriceToShow && (
            <DtvBox>
              <DtvText
                content={strikePriceToShow}
                variant={pricePoint?.strikePrice?.variant}
                sx={{
                  color: pricePoint?.strikePrice?.color || '#555555',
                  textDecoration: "line-through",
                  fontWeight: 400,
                  display: "inline-block",
                  marginRight: "8px",
                  fontSize: "14px !important",
                  marginBottom: {
 sm: "4px",
md: "0px"
}
                }}
              />
            </DtvBox>
          )}

          {/* Main Price */}
            <DtvBox sx={{
 display: 'flex',
alignItems: 'baseline',
marginTop: "-4px"
}}>
              <DtvText
                content={mainPrice}
                variant={pricePoint?.variant}
                sx={{
                  fontWeight: 700,
                  color: '#0062E3',
                  display: "inline",
                  fontSize: {
 xs: '32px',
sm: '36px'
},
                  fontFamily: "PF DIN Text Pro",
                  letterSpacing: "-0.72px"
                }}
              />
              {decimalPrice && (
                <DtvText
                  content={decimalPrice}
                  variant={pricePoint?.spanText?.variant}
                  sx={{
                    color: pricePoint?.spanText?.color || '#0062E3',
                    display: "inline",
                    fontWeight: "bold",
                    fontSize: "16px !important",
                    letterSpacing: "-0.32px"
                  }}
                />
              )}
              {pricePoint?.legal?.content && (
                <DtvText
                  content={pricePoint.legal.content}
                  variant={pricePoint?.legal?.variant}
                  sx={{
                    display: "inline",
                    fontSize: "12px !important",
                    marginLeft: "4px"
                  }}
                  tooltip={{
                    content: pricePoint?.legal?.tooltip?.[0]?.content ?? pricePoint?.legal?.tooltip?.content,
                    mode: (pricePoint?.legal?.tooltip?.[0]?.mode ?? pricePoint?.legal?.tooltip?.mode) as 'light' | 'dark'
                  }}
                />
              )}
            </DtvBox>

          {/* Legal Text */}
          {column?.legalText && (
            <DynamicText
              component={column.legalText?.component || "div"}
              content={column.legalText?.content}
              id={`${column.id}-legalText`}
              variant={column.legalText?.variant || "body2"}
              cta={{
                ...column.legalText?.cta,
                sx: {
                  color: column.legalText?.cta?.color || "#555555 !important",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit"
                }
              }}
              sx={{
                color: column.legalText?.color || '#555555 !important',
                lineHeight: "15.6px",
                fontSize: '12px !important',
                "& a:focus, & a:hover": {
                  fontWeight: 'bold'
                }
              }}
            />
          )}
        </DtvBox>
        )}
        {/* CTA Section */}
        {column?.cta?.label && (
          <DtvBox sx={{
 width: '100%',
display: "block"
}}>
            <DtvButton
              ariaLabel={column.cta?.ariaLabel || column.cta?.label}
              id={`${props?.id}-primaryCta`}
              label={column.cta?.label}
              variant={column.cta?.variant || "uxprimary" as IDtvButtonProps["variant"]}
              analytics={{
                linkName: column?.cta?.label?.toLowerCase(),
                linkDestination: column?.cta?.targeturl,
                linkPosition: 'genrePackDetailsPanel'
              }}
              sx={{
                width: isTablet ? '270px' : {
 xs: '100%',
md: '271px'
},
                minWidth: "240px",
                minHeight: "56px",
                fontSize: '16px',
                fontWeight: 500,
                borderRadius: '35px',
                backgroundColor: colorPalette.newBlue[600],
                // neutral_white (#FFFFFF), not white (#FEFEFE): 4.53:1 on newBlue[600], WCAG AA pass.
                color: colorPalette.neutral_white.main,
                ...(column.cta?.sx || {})
              }}
              {...column?.cta}
            />
          </DtvBox>
        )}
      </DtvBox>
    );
  };

  // Tablet custom layout: 1st & 2nd column stacked left, 3rd column right
  if (isTablet && sortedColumns.length === 3) {
    return (
        <DtvBox sx={{ margin: "24px 32px" }}>
          <DtvBox
            id={id}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              overflow: 'hidden',
              alignItems: 'stretch',
              borderRadius: 2
            }}
          >
            {/* Left: 1st & 2nd columns stacked */}
            <DtvBox sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 0%',
              width: '50%',
              flexBasis: '50%',
              gap: 2,
              justifyContent: 'center',
              paddingLeft: 3,
              paddingRight: 3
            }} >
              {sortedColumns?.slice(0, 2)?.map((column, idx) => renderColumn(column, idx, true))}
            </DtvBox>
            {/* Right: 3rd column */}
            <DtvBox sx={{
              flex: '1 1 0%',
              flexBasis: '50%',
              display: 'flex',
              width: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '32px',
              paddingRight: '32px'
            }}>
              {renderColumn(sortedColumns[2], 2, false)}
            </DtvBox>
          </DtvBox>
        </DtvBox>
    );
  }
  return (
      <DtvBox sx={{
 margin: {
 xs: '24px 20px',
sm: '24px 32px',
md: '24px 140px'
}
}}>
        <DtvBox
          id={id}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: shouldStack ? 'column' : 'row',
            alignItems: 'stretch',
            borderRadius: 2,
            overflow: 'hidden',
            gap: {
 md: 3,
lg: 4
}
          }}
        >
          {sortedColumns?.map((column, index) => renderColumn(column, index, false))}
        </DtvBox>
      </DtvBox>
  );
}

export default GenrePackDetailsPanel;
